/**
 * Guided onboarding tour for the Catalogue PWA (react-joyride).
 *
 * - Auto-starts the Catalogue tour on first login and after a version bump.
 * - Auto-starts a short Lookbook tour on first visit to /lookbook.
 * - Users can replay either tour on demand via startTour() (e.g. the "?" button).
 *
 * Versioning: bump TOUR_VERSION whenever tour content meaningfully changes so
 * existing users see the new tour once (matches the "after app update" request).
 */
import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Joyride, { ACTIONS, EVENTS, STATUS, type CallBackProps, type Step } from 'react-joyride';
import { useAuth } from '../shared/contexts/AuthContext';

export const TOUR_VERSION = 1;
export type TourName = 'catalogue' | 'lookbook';

const seenKey = (name: TourName) => `catalogue-tour:${name}`;

const hasSeenCurrent = (name: TourName): boolean => {
  try {
    return Number(localStorage.getItem(seenKey(name))) >= TOUR_VERSION;
  } catch {
    return true; // if storage is unavailable, don't nag the user
  }
};

const markSeen = (name: TourName) => {
  try {
    localStorage.setItem(seenKey(name), String(TOUR_VERSION));
  } catch {
    /* ignore */
  }
};

interface TourContextValue {
  startTour: (name: TourName) => void;
}

const TourContext = createContext<TourContextValue>({ startTour: () => {} });

export const useTour = () => useContext(TourContext);

export const TourProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { t } = useTranslation();
  const { isAuthenticated, mustChangePassword } = useAuth();
  const location = useLocation();

  const [run, setRun] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);
  const [tourName, setTourName] = useState<TourName>('catalogue');
  const autoStarted = useRef<Record<TourName, boolean>>({ catalogue: false, lookbook: false });

  const buildSteps = useCallback(
    (name: TourName): Step[] => {
      const titled = (key: string, target: string, extra?: Partial<Step>): Step => ({
        target,
        title: t(`tour.${key}.title`),
        content: t(`tour.${key}.body`),
        disableBeacon: true,
        ...extra,
      });

      if (name === 'lookbook') {
        return [
          titled('lb.welcome', 'body', { placement: 'center' }),
          titled('lb.nav', '[data-tour="lb-nav"]'),
          titled('lb.add', '[data-tour="lb-add"]'),
          titled('lb.portfolios', '[data-tour="lb-portfolios"]'),
          titled('lb.pdf', '[data-tour="lb-pdf"]'),
          titled('lb.done', 'body', { placement: 'center' }),
        ];
      }

      return [
        titled('welcome', 'body', { placement: 'center' }),
        titled('lang', '[data-tour="lang"]'),
        titled('search', '[data-tour="search"]'),
        titled('live', '[data-tour="live-only"]'),
        titled('filters', '[data-tour="filters"]'),
        titled('toggles', '[data-tour="display-toggles"]'),
        titled('grid', '[data-tour="grid"]'),
        titled('selection', '[data-tour="selection"]'),
        titled('pdf', '[data-tour="pdf"]'),
        titled('lookbook', '[data-tour="open-lookbook"]'),
        titled('sync', '[data-tour="sync"]'),
        titled('help', '[data-tour="help"]'),
        titled('done', 'body', { placement: 'center' }),
      ];
    },
    [t],
  );

  const [steps, setSteps] = useState<Step[]>(() => buildSteps('catalogue'));

  const startTour = useCallback(
    (name: TourName) => {
      setTourName(name);
      setSteps(buildSteps(name));
      setStepIndex(0);
      // Defer so any freshly navigated page has time to mount its targets.
      setRun(false);
      window.setTimeout(() => setRun(true), 150);
    },
    [buildSteps],
  );

  // Auto-start on first login / first lookbook visit (once per session per tour).
  useEffect(() => {
    if (!isAuthenticated || mustChangePassword) return;

    const tryAuto = (name: TourName, delay: number) => {
      if (autoStarted.current[name] || hasSeenCurrent(name)) return;
      autoStarted.current[name] = true;
      window.setTimeout(() => startTour(name), delay);
    };

    if (location.pathname === '/') tryAuto('catalogue', 900);
    else if (location.pathname === '/lookbook') tryAuto('lookbook', 700);
  }, [isAuthenticated, mustChangePassword, location.pathname, startTour]);

  const handleCallback = useCallback(
    (data: CallBackProps) => {
      const { action, index, status, type } = data;
      if (status === STATUS.FINISHED || status === STATUS.SKIPPED) {
        setRun(false);
        setStepIndex(0);
        markSeen(tourName);
      } else if (type === EVENTS.STEP_AFTER || type === EVENTS.TARGET_NOT_FOUND) {
        setStepIndex(index + (action === ACTIONS.PREV ? -1 : 1));
      }
    },
    [tourName],
  );

  return (
    <TourContext.Provider value={{ startTour }}>
      {children}
      <Joyride
        steps={steps}
        run={run}
        stepIndex={stepIndex}
        continuous
        showSkipButton
        showProgress
        scrollToFirstStep
        disableScrollParentFix
        callback={handleCallback}
        locale={{
          back: t('tour.btn.back'),
          close: t('tour.btn.close'),
          last: t('tour.btn.last'),
          next: t('tour.btn.next'),
          skip: t('tour.btn.skip'),
        }}
        styles={{
          options: {
            primaryColor: '#363D4C',
            textColor: '#363D4C',
            zIndex: 20000,
            arrowColor: '#fff',
            backgroundColor: '#fff',
          },
          tooltipTitle: { fontWeight: 700, fontSize: '1rem' },
          buttonNext: { borderRadius: 6 },
        }}
      />
    </TourContext.Provider>
  );
};

export default TourProvider;
